import { mutate } from "swr";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { switchTenantApi } from "@/api/endpoints/users";
import { UserProfile } from "@/api/types";
import { getUserProfileFromToken, isAuthenticated, setTokens } from "@/lib/auth";
import { Config } from "@/lib/config";
import { setPostHogUser } from "@/lib/posthog";
import { setSentryUser } from "@/lib/sentry";
import { useCartStore } from "@/stores/cartStore";
import { useServiceAddressOverrideStore } from "@/stores/serviceAddressOverrideStore";
import { useServiceDetailAnswersStore } from "@/stores/serviceDetailAnswersStore";

const IBH_SELECTED_TENANT_KEY = "ibh_selected_tenant";

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userProfile: UserProfile | null;
  tenant: string;
}

interface AuthActions {
  login: () => void;
  logout: () => void;
  checkAuth: () => void;
  setLoading: (loading: boolean) => void;
  switchTenant: (tenant: string) => Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  userProfile: null,
  tenant: "",
};

/**
 * Determines the initial tenant for a logged-in user
 * For internal instances, checks localStorage first, then falls back to first available tenant
 */
const getInitialTenant = (profile: UserProfile): string => {
  if (!Config.isInternalInstance) {
    return profile.tenant || "";
  }

  const persisted = localStorage.getItem(IBH_SELECTED_TENANT_KEY);
  if (persisted) {
    return persisted;
  }

  if (profile.tenant) {
    return profile.tenant;
  }

  return Config.available_tenants?.[0]?.id ?? "";
};

/**
 * Updates monitoring services (Sentry, PostHog) with user information
 */
const updateMonitoringServices = (profile: UserProfile, tenant: string): void => {
  setSentryUser({
    email: profile.email,
    tenant,
  });

  setPostHogUser({
    email: profile.email,
    tenant,
  });
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => {
      const login = () => {
        const profile = getUserProfileFromToken();
        if (!profile) {
          throw new Error("User profile not found");
        }

        const tenant = getInitialTenant(profile);

        set(
          {
            isAuthenticated: true,
            isLoading: false,
            userProfile: profile,
            tenant,
          },
          undefined,
          "auth/login",
        );

        updateMonitoringServices(profile, tenant);

        useCartStore
          .getState()
          .initializeCartsOnLogin()
          .catch((error) => {
            console.error("Failed to initialize carts on login:", error);
          });
      };

      const logout = () => {
        useCartStore.getState().clearAllCarts();
        useServiceAddressOverrideStore.getState().clearAllOverrides();
        localStorage.removeItem(IBH_SELECTED_TENANT_KEY);

        set({ ...initialState, isLoading: false }, undefined, "auth/logout");
      };

      const checkAuth = () => {
        const authenticated = isAuthenticated();

        if (authenticated) {
          login();
        } else {
          set({ ...initialState }, undefined, "auth/checkAuth not authenticated");
        }
      };

      const switchTenant = async (newTenant: string) => {
        if (!Config.isInternalInstance) return;

        const tokenData = await switchTenantApi(newTenant);

        setTokens({ access: tokenData.access, refresh: tokenData.refresh });

        const profile = getUserProfileFromToken();
        if (!profile) {
          throw new Error("User profile not found after tenant switch");
        }

        localStorage.setItem(IBH_SELECTED_TENANT_KEY, newTenant);
        useCartStore.getState().clearAllCarts();
        useServiceAddressOverrideStore.getState().clearAllOverrides();
        useServiceDetailAnswersStore.getState().clearAll();
        mutate(() => true, undefined, { revalidate: false });

        set(
          {
            tenant: newTenant,
            userProfile: profile,
          },
          undefined,
          "auth/switchTenant",
        );

        updateMonitoringServices(profile, newTenant);
      };

      return {
        ...initialState,
        setLoading: (loading: boolean) => set({ isLoading: loading }, undefined, "auth/setLoading"),
        checkAuth,
        login,
        switchTenant,
        logout,
      };
    },
    {
      name: "auth-store",
    },
  ),
);

/**
 * Handles cross-tab tenant changes via localStorage events
 */
const handleStorageChange = (event: StorageEvent): void => {
  if (event.key !== IBH_SELECTED_TENANT_KEY) return;
  if (!event.newValue) return;

  const state = useAuthStore.getState();
  if (!state.isAuthenticated || state.tenant === event.newValue) return;

  window.location.reload();
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", handleStorageChange);
}
